import { UTRON_DUMMY_EMAIL } from "../config/Config";
import { ErrorEntity } from "../entities/ServicesEntities/ErrorEntity";
import { DIALING_CODES } from "../enums/Enums";
import { getDecodedHtmlString, getHtmlTags, removeHtmlTags } from "./CommonUtils";
import Strings from "../resources/Strings";
import sanitizeHtml from "sanitize-html";

export const getErrorMessage = (error) => {
  if (error) {
    return !error.isValid ? error.errorMessage : "";
  }
};

export const inputHasValue = (input) => {
  const isArray = Array.isArray(input);
  let hasValue = false;

  if (isArray) {
    hasValue = arrayNotEmpty(input)?.isValid;
  }
  else {
    hasValue = isRequired(input)?.isValid;
  }

  return hasValue;
};

export const isRequired = (input) => {
  return new ErrorEntity(
    input !== null && input !== undefined && input !== "",
    Strings.INPUT_ERROR_MESSAGES.IS_REQUIRED
  );
};

export const isNumber = (input) => {
  let reg = /^\d+$/;
  return new ErrorEntity(
    reg.test(input),
    Strings.INPUT_ERROR_MESSAGES.IS_NUMBER
  );
};

export const arrayNotEmpty = (input) => {
  return new ErrorEntity(
    input !== null && input !== undefined && input.length !== 0,
    Strings.INPUT_ERROR_MESSAGES.IS_REQUIRED
  );
};

export const isCarNicknameValid = (input) => {
  let reg = /[a-zA-Z]+/; // At least one character
  return new ErrorEntity(
    reg.test(input),
    Strings.INPUT_ERROR_MESSAGES.INVALID_CAR_NICKNAME
  );
};

export const isLicensePlateValid = (input) => {
  let reg = /^[0-9A-Za-z-]+$/;
  return new ErrorEntity(
    reg.test(input) && input.length <= 30,
    Strings.INPUT_ERROR_MESSAGES.INVALID_CAR_LICENSE
  );
};

// Disallow whitespaces on start / end or both (of a string), between strings is allowed
export const isWhitespacesValid = (input) => {
  //let reg = /^\s+|\s+$/; // only whitespace with no any other restrictions
  let reg = /^[<>/\s]+|[<>/\s]+$/;
  // <> / - (is not allowed)
  return new ErrorEntity(
    !reg.test(input),
    Strings.INPUT_ERROR_MESSAGES.INVALID_FIELD
  );
};

// Checks whitespaces, allow name & number, except '<' '>'
export const isLengthCharValid = (input, length) => {
  return new ErrorEntity(
    isWhitespacesValid(input)?.isValid && isTextMessageValid(input)?.isValid,
    `${Strings.INPUT_ERROR_MESSAGES.INVALID_LENGTH_CHAR}`.format(length)
  );
};

export const isLengthValid = (input, maxLength, excludeHtmlTags = false) => {
  if (excludeHtmlTags) {
    input = getDecodedHtmlString(removeHtmlTags(input));
  }

  return new ErrorEntity(
    input.length <= maxLength,
    Strings.INPUT_ERROR_MESSAGES.INVALID_LENGTH
  );
};

export const isNameValid = (input) => {
  let reg = /^[a-zA-Z\s\-]{0,30}$/; // no special character and numbers, max length 30
  return new ErrorEntity(
    reg.test(input),
    Strings.INPUT_ERROR_MESSAGES.INVALID_INPUT
  );
};

export const isTextValid = (input) => {
  let reg = /^[0-9A-Za-z-./'`,\s]{0,300}$/;
  return new ErrorEntity(
    reg.test(input) ,//&& (!input || input == null || input.length <= 300),
    Strings.INPUT_ERROR_MESSAGES.INVALID_NOTE
  );
};

export const isTextMessageValid = (input) => {
  // allow charachters, numbers and special chars
  // <> - (is not allowed)
  let reg = /^[a-zA-Z0-9\s!@#$%^&*()_+\-=\[\]{};':"\\|,.\/?]+$/;
  return new ErrorEntity(
    reg.test(input) && input.trim() !== '',  
    Strings.INPUT_ERROR_MESSAGES.INVALID_TEXT_MESSAGE
  );
};

export const isHtmlValid = (input) => {
  // Check if there is any not allowed tags
  // decodes the encoded made (or escaped) string by the received component
  //let isManualTags = input?.includes('&lt;' || '&nbsp;' || '&amp;' || '&nbsp;');

  let decodedHtmlString = getDecodedHtmlString(input);

  // Find any tags on the string
  let foundedTags = getHtmlTags(decodedHtmlString);

  // Defines which tags & attributes is allowed, 
  // div / span / br - allows to copy text from other resource
  let sanitizeOptions = {
    allowedTags: ["b", "u", "i", "div", "span", "br", "p", "font"],
    allowedAttributes: { font: ["size"] }
  };

  // Clean the tags from open & close (<, > ,/) 
  let cleanTags = foundedTags?.map(str => str?.replace('<', '').replace('>', '').replace('/', ''));
  
  // Distinct array
  cleanTags = cleanTags?.filter((tag, index) => cleanTags.findIndex((ft) => ft === tag) === index); 

  // Compare to check if there is any manual typed and not allowed tags
  let isManualTagsAllowed = cleanTags?.some(ct => sanitizeOptions.allowedTags.some(atag => ct === atag));
  
  // Sanitizing result options:
  // 1.case after sanitize the result is empty, means all string content has been wrapped with not allowed tags
  // 2.case only partial content has been removed, and there is remaining valid content
  let sanitizeRes = sanitizeHtml(decodedHtmlString, sanitizeOptions);

  return new ErrorEntity(
    sanitizeRes === '' || sanitizeRes.trim() === '' ? false : true && isManualTagsAllowed, 
    Strings.INPUT_ERROR_MESSAGES.INVALID_BODY
  );
};


export const isPhoneNumber = (input) => {
  let reg;

  if (input.startsWith(DIALING_CODES.IL)) {
    //reg = /\(?([0-9]{3})\)?([ .-]?)([0-9]{3})\2([0-9]{4})/; // At least one character
    
    // must start with 972 ( il dial code) or 05
    // the '0' is already removed by phoneNumberToSend when received here
    reg = /^(9725)(\d){8}$/;
  }

  if (input.startsWith(DIALING_CODES.US)) {
    // 1 - us dial code,
    // 2-9 area code -  (first number must start from 2 digit and above (us country area codes)
    // {9} - exactly 9 digits
    reg = /^(([1])[2-9])(\d){9}$/;
  }  

  //let reg = /\(?([0-9]{3})\)?([ .-]?)([0-9]{3})\2([0-9]{4})/; // At least one character
  // return reg.test(input);
  return new ErrorEntity(
    reg.test(input),
    Strings.INPUT_ERROR_MESSAGES.INVALID_PHONE
  );
};

export const isSameUserPhoneNumberError = () => {
  return new ErrorEntity(
    false,
    Strings.INPUT_ERROR_MESSAGES.INVALID_SAME_PHONE
  );
}

export const isDummyEmail = (input) => {
  return new ErrorEntity(
    input.endsWith(UTRON_DUMMY_EMAIL),
    Strings.INPUT_ERROR_MESSAGES.INVALID_EMAIL
  );
};

export const isEmail = (input) => {
  let reg =  /^([\w-]+(?:\.[\w-]+)*)@((?:[\w-]+\.)*\w[\w-]{0,66})\.([a-z]{2,6}(?:\.[a-z]{2})?)$/i //OLD REG - /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;
  return new ErrorEntity(
    reg.test(input),
    Strings.INPUT_ERROR_MESSAGES.INVALID_EMAIL
  );
};

export const isValidDate = (date) => {
  return date instanceof Date && date.toString() !== 'Invalid Date';
}

export const isValidPassword = (newPassword, confirmPassword) => {
  let reg = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{8,}$/;
  if (newPassword !== confirmPassword) {
    return new ErrorEntity(
      newPassword === confirmPassword,
      Strings.INPUT_ERROR_MESSAGES.UNMATCHED_PASSWORDS
    );
  }

  return new ErrorEntity(
    reg.test(newPassword),
    Strings.INPUT_ERROR_MESSAGES.INVALID_PASSWORD
  );
};

// Receives fileSize in bytes, maxFileSize in KB, and checks if exceeded max limit
export const isFileSizeValid = (fileSize, maxFileSize) => {
  const fileSizeInKb = fileSize / 1024; // convert bytes to kilo-bytes 

  return new ErrorEntity(
    fileSizeInKb <= maxFileSize, 
    `${Strings.INPUT_ERROR_MESSAGES.INVALID_FILE_SIZE}`.format(`(${maxFileSize} ${Strings.FILE_SIZE_KB})`)
  );
};

// Receives the file name with extension included (name.extension), and the valid extension file types to compare
export const isFileTypeValid = (fileName, validExtensionFileTypes) => {
  // also checks with no case sensitive 
  return new ErrorEntity(
    Object.values(validExtensionFileTypes)?.some(ft => fileName?.toLowerCase()?.endsWith(ft)), 
    Strings.INPUT_ERROR_MESSAGES.INVALID_FILE_TYPE
  );
};

